import { Link } from 'react-router-dom';
import styles from './index.module.scss';
import SiteTopNav from '@/components/SiteTopNav';

const EngineeredVsSolidHardwood = () => {
  return (
    <div className={styles['article-page']}>
      <div className={styles.page}>
        <header className={styles['article-header']}>
          <SiteTopNav variant="light" />
        </header>

        <main className={styles['article-main']}>
          <Link className={styles['article-back']} to="/academy/hardwood-basics">
            ← Back to Flooring 101
          </Link>

          <p className={styles['article-eyebrow']} data-motion="copy">
            Sakura Floors Academy
          </p>

          <h1 className={styles['article-title']} data-motion="hero">
            Engineered Hardwood vs. Solid Hardwood Flooring
          </h1>

          <p className={styles['article-intro']} data-motion="copy">
            One of the first decisions you&rsquo;ll make when choosing hardwood flooring is whether
            to install engineered hardwood or solid hardwood. Although both feature genuine wood and
            offer timeless beauty, they are constructed differently and perform differently
            depending on the environment.
          </p>

          <p className={styles['article-intro']} data-motion="copy">
            Neither option is automatically better. The right choice depends on your subfloor,
            indoor conditions, design preferences, installation requirements, and long-term
            expectations.
          </p>

          <section className={styles['article-section']} data-motion="section">
            <p className={styles['article-section__eyebrow']}>Solid Hardwood</p>

            <h2 className={styles['article-section__title']}>What Is Solid Hardwood?</h2>

            <p className={styles['article-section__text']}>
              Solid hardwood flooring is milled from a single piece of natural wood. Each plank
              consists entirely of the same hardwood species from top to bottom.
            </p>

            <p className={styles['article-section__text']}>
              For centuries, solid hardwood has been prized for its authenticity, longevity, and
              ability to develop character over time. Because the entire plank is hardwood, it can
              typically be sanded and refinished multiple times, allowing homeowners to restore or
              update its appearance after years of use.
            </p>

            <h3 className={styles['article-section__subtitle']}>Advantages of Solid Hardwood</h3>

            <ul className={styles['article-list']}>
              <li>Made from a single piece of natural hardwood</li>
              <li>Can typically be refinished multiple times</li>
              <li>Exceptional lifespan when properly installed and maintained</li>
              <li>Traditional construction with timeless appeal</li>
              <li>Can add long-term value to a home</li>
            </ul>

            <h3 className={styles['article-section__subtitle']}>Limitations of Solid Hardwood</h3>

            <p className={styles['article-section__text']}>
              Natural wood expands and contracts as humidity changes throughout the year. In dry
              conditions, boards may shrink slightly and create small seasonal gaps. In humid
              conditions, they expand, which can lead to cupping, crowning, or warping when moisture
              is excessive or indoor conditions are not properly controlled.
            </p>

            <p className={styles['article-section__text']}>
              For this reason, solid hardwood is generally recommended for climate-controlled,
              above-grade living spaces. It is usually not the preferred option for basements,
              direct installation over concrete slabs, or environments with substantial moisture and
              humidity fluctuations.
            </p>
          </section>

          <section className={styles['article-section']} data-motion="section">
            <p className={styles['article-section__eyebrow']}>Engineered Hardwood</p>

            <h2 className={styles['article-section__title']}>What Is Engineered Hardwood?</h2>

            <p className={styles['article-section__text']}>
              Engineered hardwood is also real hardwood, but instead of being milled from one solid
              piece of wood, each plank is constructed in multiple layers.
            </p>

            <p className={styles['article-section__text']}>
              A genuine hardwood wear layer forms the visible surface. Beneath it is a multi-layer
              plywood core, with each layer positioned in alternating grain directions. This
              cross-ply construction helps reduce the natural movement caused by changes in
              temperature and humidity.
            </p>

            <p className={styles['article-section__text']}>
              The result is a floor that provides the authentic appearance and character of solid
              wood while offering greater dimensional stability across changing environmental
              conditions.
            </p>

            <p className={styles['article-section__text']}>
              At Sakura Floors, our engineered hardwood features carefully selected hardwood wear
              layers bonded to premium multi-layer eucalyptus plywood cores. This construction
              provides dependable stability without compromising the natural beauty of genuine wood.
            </p>

            <h3 className={styles['article-section__subtitle']}>
              Advantages of Engineered Hardwood
            </h3>

            <ul className={styles['article-list']}>
              <li>Genuine hardwood surface</li>
              <li>Greater dimensional stability</li>
              <li>Better performance during seasonal humidity changes</li>
              <li>Suitable for installation over properly prepared concrete</li>
              <li>Compatible with approved radiant-heating systems</li>
              <li>Well suited to wider and longer plank formats</li>
              <li>Supports glue-down, nail-down, and approved floating installations</li>
            </ul>

            <h3 className={styles['article-section__subtitle']}>Considerations</h3>

            <p className={styles['article-section__text']}>
              The refinishing potential of engineered hardwood depends primarily on the thickness of
              its hardwood wear layer and the condition of the floor. High-quality engineered
              flooring with a substantial wear layer may be refinished one or more times during its
              service life.
            </p>

            <p className={styles['article-section__text']}>
              Engineered hardwood is more dimensionally stable than solid hardwood, but it is still
              a natural wood floor. It is not waterproof and should be protected from standing
              water, plumbing leaks, and excessive moisture.
            </p>
          </section>

          <section className={styles['article-section']} data-motion="section">
            <p className={styles['article-section__eyebrow']}>Comparison</p>

            <h2 className={styles['article-section__title']}>Construction Comparison</h2>

            <p className={styles['article-section__text']}>
              Both products offer the appearance and warmth of genuine wood. The most important
              differences are found beneath the surface.
            </p>

            <div className={styles['article-table-wrap']}>
              <table className={styles['article-table']}>
                <thead>
                  <tr>
                    <th scope="col">Feature</th>
                    <th scope="col">Engineered Hardwood</th>
                    <th scope="col">Solid Hardwood</th>
                  </tr>
                </thead>

                <tbody>
                  <tr>
                    <th scope="row">Surface</th>
                    <td>Genuine hardwood wear layer</td>
                    <td>Solid hardwood throughout</td>
                  </tr>

                  <tr>
                    <th scope="row">Core</th>
                    <td>Multi-layer plywood</td>
                    <td>Single piece of hardwood</td>
                  </tr>

                  <tr>
                    <th scope="row">Dimensional stability</th>
                    <td>Excellent</td>
                    <td>More sensitive to environmental changes</td>
                  </tr>

                  <tr>
                    <th scope="row">Humidity tolerance</th>
                    <td>Better tolerance of normal seasonal changes</td>
                    <td>Requires more consistent indoor conditions</td>
                  </tr>

                  <tr>
                    <th scope="row">Radiant-heat compatibility</th>
                    <td>Often compatible when approved by the manufacturer</td>
                    <td>Limited and project dependent</td>
                  </tr>

                  <tr>
                    <th scope="row">Installation over concrete</th>
                    <td>Suitable with the correct preparation and installation system</td>
                    <td>Generally not installed directly over concrete</td>
                  </tr>

                  <tr>
                    <th scope="row">Wide-plank availability</th>
                    <td>Excellent</td>
                    <td>More limited because of natural wood movement</td>
                  </tr>

                  <tr>
                    <th scope="row">Refinishing</th>
                    <td>Depends on wear-layer thickness</td>
                    <td>Can typically be refinished multiple times</td>
                  </tr>

                  <tr>
                    <th scope="row">Installed appearance</th>
                    <td>Authentic natural hardwood</td>
                    <td>Authentic natural hardwood</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          <section className={styles['article-section']} data-motion="section">
            <p className={styles['article-section__eyebrow']}>Longevity</p>

            <h2 className={styles['article-section__title']}>Which Flooring Lasts Longer?</h2>

            <p className={styles['article-section__text']}>
              Many homeowners assume solid hardwood automatically lasts longer, but the practical
              lifespan of any wood floor depends on product quality, installation, maintenance,
              moisture control, traffic, and the conditions inside the home.
            </p>

            <p className={styles['article-section__text']}>
              A professionally installed, high-quality engineered hardwood floor can last 30 to 50
              years or longer with proper care. Solid hardwood may remain in service even longer
              because it can usually be refinished more times, but that advantage matters only when
              the floor is installed in an environment where it remains stable.
            </p>

            <p className={styles['article-section__text']}>
              In many contemporary homes, particularly those built on concrete slabs or equipped
              with radiant heating, engineered hardwood may provide more dependable long-term
              performance because it is less susceptible to movement caused by changing humidity.
            </p>
          </section>

          <section className={styles['article-section']} data-motion="section">
            <p className={styles['article-section__eyebrow']}>Environment</p>

            <h2 className={styles['article-section__title']}>
              Which Performs Better in Different Environments?
            </h2>

            <div className={styles['article-feature-list']}>
              <div className={styles['article-feature']}>
                <h3 className={styles['article-feature__title']}>Choose Solid Hardwood If</h3>

                <ul className={styles['article-list']}>
                  <li>Your home has a plywood or wood subfloor</li>
                  <li>Indoor humidity remains stable throughout the year</li>
                  <li>You want the ability to refinish the floor numerous times</li>
                  <li>You are restoring a traditional or historic home</li>
                  <li>You prefer traditional solid-wood construction</li>
                </ul>
              </div>

              <div className={styles['article-feature']}>
                <h3 className={styles['article-feature__title']}>Choose Engineered Hardwood If</h3>

                <ul className={styles['article-list']}>
                  <li>Your home is built on a concrete slab</li>
                  <li>You plan to install approved radiant floor heating</li>
                  <li>You prefer wide or long plank flooring</li>
                  <li>You live in an area with seasonal humidity changes</li>
                  <li>You want greater stability with the appearance of real hardwood</li>
                </ul>
              </div>
            </div>
          </section>

          <section className={styles['article-section']} data-motion="section">
            <p className={styles['article-section__eyebrow']}>Modern Luxury</p>

            <h2 className={styles['article-section__title']}>
              Why Many Luxury Homes Choose Engineered Hardwood
            </h2>

            <p className={styles['article-section__text']}>
              Engineered hardwood has become a preferred flooring option for many luxury residential
              projects, not because it looks different from solid wood, but because its construction
              is well suited to modern building methods and architectural preferences.
            </p>

            <p className={styles['article-section__text']}>
              Large-format windows, open-concept layouts, concrete foundations, radiant heating
              systems, and wider plank designs can all benefit from the increased dimensional
              stability of engineered construction.
            </p>

            <p className={styles['article-section__text']}>
              Because engineered hardwood is less susceptible to expansion, contraction, and cupping
              during normal seasonal changes, architects, designers, and builders often specify it
              for high-end residential projects where long-term performance is as important as
              appearance.
            </p>
          </section>

          <section className={styles['article-section']} data-motion="section">
            <p className={styles['article-section__eyebrow']}>Our Recommendation</p>

            <h2 className={styles['article-section__title']}>The Sakura Floors Perspective</h2>

            <p className={styles['article-section__text']}>
              At Sakura Floors, we specialize in premium engineered hardwood because we believe it
              offers an ideal balance of natural beauty, structural stability, installation
              flexibility, and modern performance.
            </p>

            <p className={styles['article-section__text']}>
              Our collections combine authentic hardwood wear layers with precision-engineered
              multi-layer plywood cores. The result preserves the warmth, grain, and character of
              real wood while adapting to the demands of contemporary homes.
            </p>

            <p className={styles['article-section__text']}>
              Whether you are building a custom residence, renovating a family home, or designing a
              luxury interior, engineered hardwood provides the confidence of genuine wood with the
              stability today&rsquo;s projects require.
            </p>
          </section>

          <section className={styles['article-section']} data-motion="section">
            <p className={styles['article-section__eyebrow']}>Final Decision</p>

            <h2 className={styles['article-section__title']}>
              Engineered or Solid: Which Should You Choose?
            </h2>

            <p className={styles['article-section__text']}>
              Choose solid hardwood when you value traditional construction, have a suitable wood
              subfloor, maintain consistent indoor humidity, and want maximum long-term refinishing
              potential.
            </p>

            <p className={styles['article-section__text']}>
              Choose engineered hardwood when you need greater dimensional stability, plan to
              install over concrete or approved radiant heating, prefer wide-plank designs, or want
              a floor suited to modern construction.
            </p>

            <p className={styles['article-section__text']}>
              Both are genuine hardwood floors. The best choice is the one that fits your home,
              installation conditions, lifestyle, and long-term expectations.
            </p>
          </section>
        </main>
      </div>
    </div>
  );
};

export default EngineeredVsSolidHardwood;
